import { Activity } from "../../components/activity/types";
import { FactorSearchFormFields } from "../../components/factor/factor-search-form/types";
import { useDashboardStore } from "../../stores";
import {
  Cheque,
  ChequeBuilder,
  ChequeBuilderBackend,
  ChequeSelectorItem,
  Customer,
  Factor,
  ObjectId,
} from "../../types";
import Api from "../api";
import { createFormData, getFileHeaders, parseApiData } from "../utils";

class FactorApi extends Api {
  static recv(
    customer: Customer,
    description: string,
    cheques: Array<ChequeBuilder>,
    files: Array<FileList | null>
  ): Promise<void> {
    let parsedFiles = this.buildFiles(files);
    const headers = getFileHeaders(parsedFiles as unknown as FileList);

    return new Promise((resolve, reject) => {
      this.postData<Factor, unknown>(
        "/factor/receive",
        createFormData({
          customer: customer._id.$oid,
          description,
          "cheques[]": this.buildCheques(cheques),
          "images[]": parsedFiles,
        }),
        { headers }
      )
        .then((res) => {
          useDashboardStore.getState().activitiesAdd(res.activities[0]);
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  static updateRecvFactor(
    id: string,
    customer: Customer | null,
    description: string,
    delCheques: Array<ChequeSelectorItem>,
    newCheques: Array<ChequeBuilder>,
    newFiels: Array<FileList | null>
  ): Promise<Factor> {
    let parsedFiles = this.buildFiles(newFiels);
    const headers = getFileHeaders(parsedFiles as unknown as FileList);

    return new Promise((resolve, reject) => {
      this.patchData<Factor, unknown>(
        `/factor/received/${id}`,
        createFormData({
          customer: customer?._id.$oid,
          description,
          "del_cheques[]": this.buildChequeSelectorItemWithDelete(delCheques),
          "new_cheques[]": this.buildCheques(newCheques),
          "new_images[]": parsedFiles,
        }),
        { headers }
      )
        .then((res) => {
          useDashboardStore.getState().activitiesAdd(res.activities[0]);
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  static getCheques(id: ObjectId): Promise<Array<Cheque>> {
    return new Promise((resolve, reject) => {
      this.getData<{ cheques: Array<Cheque> }>(`/factor/cheques/${id.$oid}`)
        .then((res) => {
          resolve(res.cheques);
        })
        .catch((err) => reject(err));
    });
  }

  static search(query: FactorSearchFormFields): Promise<Array<Factor>> {
    return this.postData(
      "/factor/search",
      parseApiData({
        customer: query.customer?._id.$oid,
        description: query.description,
        total_price: query.totalPrice,
        type: query.type?.name,
      })
    );
  }

  static updateSpentFactor(
    id: string,
    customer: Customer | null,
    description: string,
    del_cheques: Array<ChequeSelectorItem>,
    new_cheques: Array<ChequeSelectorItem>
  ): Promise<Factor> {
    return new Promise((resolve, reject) => {
      this.patchData<Factor, unknown>(
        `/factor/spent/${id}`,
        parseApiData({
          customer: customer?._id.$oid,
          description,
          del_cheques: this.buildChequeSelectorItems(del_cheques),
          new_cheques: this.buildChequeSelectorItems(new_cheques),
        })
      )
        .then((res) => {
          useDashboardStore.getState().activitiesAdd(res.activities[0]);
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  static getById(id: string): Promise<Factor> {
    return this.getData(`/factor/${id}`);
  }

  static spend(
    customerId: string,
    description: string,
    cheques: Array<ChequeSelectorItem>
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.postData<Factor, unknown>("/factor/spend", {
        customer: customerId,
        description,
        cheques: this.buildChequeSelectorItems(cheques),
      })
        .then((res) => {
          useDashboardStore.getState().activitiesAdd(res.activities[0]);
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  static deleteFactor(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.deleteData<Activity>(`/factor/${id}`)
        .then((res) => {
          useDashboardStore.getState().activitiesAdd(res);
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  // private
  static buildCheques(
    cheques: Array<ChequeBuilder>
  ): Array<ChequeBuilderBackend> {
    return cheques.map((c) => ({ ...c, bank: c.bank?.name || "mellat" }));
  }

  static buildFiles(files: Array<FileList | null>): Array<File | null> {
    return files.map((f) => (f ? f[0] : null));
  }

  static buildChequeSelectorItems(
    items: Array<ChequeSelectorItem>
  ): Array<string> {
    return items.map((i) => i.cheque._id.$oid);
  }

  static buildChequeSelectorItemWithDelete(
    items: Array<ChequeSelectorItem>
  ): Array<{ delete: boolean; id: string }> {
    return items.map((i) => ({ id: i.cheque._id.$oid, delete: i.delete }));
  }
}

export default FactorApi;
